import _ from 'lodash'
import FileSaver from 'file-saver'
import HtmlTableParser from './html-table-parser'
import Workbook from './excel-workbook'

var ExcelExport = {

	/**
	 * @typedef ExcelExportOptions
	 *
	 * @property {Object} model
	 * An object used to map raw data to a cell using the ids derived from
	 *     opts.thAttribute and opts.trAttribute see unit test suite
	 *     '_addCellToRow' for details.
	 *
	 * @property {ExcelExportDataAttributes} dataAttributes
	 *
	 * @property {String|Object} [label='export']
	 * If multiple tables are provided this must be a hash with keys == table
	 *     identifier
	 *
	 * @property {function} [interceptRows]
	 * Allows a caller to intercept the two-dimensional array of rows and
	 *     modify the structure before being transformed into an excel sheet.
	 *     Use with caution, and do not couple to the data structure of the
	 *     cellObject which may change and is not guaranteed to be backwards
	 *     compatible.  (Initially added to remove column from dataset records
	 *     table)
	 *
	 * @property {boolean} [indexBasedIdsEnabled=false]
	 * If a table does not set trAttribute and thAttribute, this flag can be
	 *     set to match the table cells up to a model which matches the html
	 *     table based on row and column index positions. Important: only
	 *     enable this option if the model and the table have the same data
	 *     structure.
	 *
	 * @property {jQuery[]} [columnHeadings=bodyEl.parent().find('th')]
	 * Allows the caller to provide the header cells if they aren't represented
	 * by 'th' elements.
	 */

	/**
	 * @typedef ExcelExportDataAttributes
	 *
	 * @property {String} th
	 * The name of a element attribute used to find the raw data from
	 *     opts.model (i.e. data-period). Must be set on the TH element
	 *
	 * @property {String} tr
	 * The name of a element attribute used to find the raw data from
	 *     opts.model (i.e. data-id). Will be detected on any TD or A element
	 *     in the row. If subsequent cells do not have this attribute the
	 *     previous column cell value will be applied. If no value in the row
	 *     has this attribute the value from the previous row will be applied
	 *
	 * @property {String} [value='value']
	 * The name of a property belonging to the model record identified by the
	 *     'th' and 'tr' attributes. This property will be used to replace the
	 *     DOM value.
	 *
	 * @property {Number} [valuePosition=0]
	 * If a value is represented in more than one column, this attribute
	 *     indicates the position of the column that contains the raw value
	 *     which should be assigned from the model via the 'value' data
	 *     attribute. example: report thresholds label and value columns
	 *
	 * @property {Function} [modelLookup]
	 * A function that returns an object with the following structure.  It is
	 *     provided the tableId, JQuery cell object, and row index.  This model
	 *     object will take precedence over any other rules in the parser, so
	 *     if it is implemented it must return the final values for: value,
	 *     dataType, and precision of the exported cell.
	 *
	 * function(tableId, jqueryCell, rowIndex, modelRowId) {
	 * 	...
	 * 	return {
	 * 		rowId,
	 * 		value, // If no value is provided the domValue will be used
	 * 		dataType, // optional; any valid dataType (e.g. 'text', 'currency', 'percent', 'date')
	 * 		precision // optional
	 * 	}
	 * }
	 */

	/**
	 * Create an excel file and invoke the browsers save workflow.
	 *
	 * @param {jQuery} bodyEl JQuery object representing <tbody>
	 * @param {ExcelExportOptions} opts
	 *
	 * Also note that for raw values to be extracted from the model there
	 * must be a 'value' attribute on the model object.  The model is expected to
	 * be a two dimensional array of objects.
	 *
	 * @return {Promise} fulfilled once the file is saved
	 */
	saveTableAsFile: function (bodyEl, opts) {
		var sheetRows = HtmlTableParser.parse({id: 0, bodyEl}, opts || {})
		opts.interceptRows && opts.interceptRows(sheetRows)
		return this._saveFile(sheetRows, opts)
	},

	/**
	 * Use this function if there are multiple tables to export from
	 * a single view.  They will each be created on their own sheet.
	 *
	 * @param {Object[]} tableBodies Array of table objects
	 *
	 * @param {Object[]} tableBodies.id table identifier
	 * This identifier will be passed back to the dataAttributes.modelLookup
	 * function if one was provided
	 *
	 * @param {jQuery[]} tableBodies.bodyEl JQuery objects representing <tbody>

	 * @param {Object} opts Inherits all options from saveTableAsFile
	 *
	 * @param {String} opts.fileLabel If multiple tables are provided
	 * this is required and will be used to set the filename.
	 *
	 */
	saveTablesAsSheetsInFile: function (tableBodies, opts) {
		var wb = this._createWorkbook()
		_.each(tableBodies, tableObj => {
			const {id, bodyEl} = tableObj
			var sheetRows = HtmlTableParser.parse({id, bodyEl}, opts || {})
			opts.interceptRows && opts.interceptRows(sheetRows)
			var label = _.get(opts, `label[${id}]`, 'export')
			var ws = wb.createSheet(sheetRows, label)
			this._addSheetToWorkbook(wb, label, ws)
		})

		return this._saveWorkbookToFile(wb, _.defaultTo(opts.fileLabel, 'export'))
	},

	/**
	 * Adds a new row to the workbook
	 * Designed to be used in conjunction with the 'interceptRows' option.
	 *
	 * @param rows as provided from interceptRows
	 * @param rowIndex 0 based
	 */
	addRow: function(rows, rowIndex) {
		var row = []
		rows.splice(this._safeIndex(_.size(rows), rowIndex), 0, row)
		return row
	},

	/**
	 * Adds a cell to a row that will be transformed into a excel cell
	 * Designed to be used in conjunction with the 'interceptRows' option.
	 *
	 * @param {object} row use addRow() and pass the return value
	 * @param {number} colIndex 0 based
	 * @param cellOpts.value
	 *
	 * // Note: other cellOpts will be added on an as needed basis
	 */
	addCell: function(row, colIndex, cellOpts) {
		row.splice(this._safeIndex(_.size(row), colIndex), 0, this._createCell(cellOpts))
	},

	/**
	 * Clears one side of a border.  Can be conditional
	 * @param {Object} cellData
	 * @param {String} side must be one of: top, bottom, left, right
	 * @param {boolean} condition
	 */
	clearBorder: function(cellData, side, condition) {
		if (condition === undefined || condition) {
			cellData.border[side] = undefined
		}
	},

	/**
	 * Keeps cell attributes encapsulated
	 * most attributes can be found in html-table-parser#_createCellData
	 */
	_createCell: function(cellOpts) {
		return {
			value: _.get(cellOpts,'value',''),
			wrap: _.get(cellOpts,'wrap',false),
			borderless: _.get(cellOpts,'borderless',true),
			autosize: false
		}
	},

	/**
	 * Ensures elements are added to the end of the array if an index would
	 * otherwise be out of bounds
	 */
	_safeIndex: function(size, i) {
		// splice supports negative numbers, but not bounds check
		// because we don't need them yet
		return Math.min(size, i)
	},

	_saveFile: function(data, opts) {
		var label = _.defaultTo(opts.label, 'export')
		var wb = this._createWorkbook()
		var ws = wb.createSheet(data, label)
		this._addSheetToWorkbook(wb, label, ws)
		return this._saveWorkbookToFile(wb, label)
	},

	_saveWorkbookToFile: function (wb, label) {
		return new Promise( (resolve, reject) => {
			var fileBlob = new Blob(wb.getAsArrayBuffer(),
				{type: 'application/octet-stream'})
			const result = FileSaver.saveAs(fileBlob, label + '.xlsx')
			// check every 50 ms to see if the file was saved
			const intervalMillis = 50
			let tries =  5000 / intervalMillis
			const interval = setInterval(() => {
				tries--
				// IE returns true, so just resolve things or the dialog will hang
				if(result.readyState === 2 || result === true) {
					clearInterval(interval)
					resolve()
				}
				else if (tries < 0) {
					clearInterval(interval)
					reject('File was not saved after 5 seconds, giving up')
				}
			}, intervalMillis)
		})

	},

	_createWorkbook: function () {
		var wb = new Workbook()
		wb.Sheets = {}
		wb.SheetNames = []
		return wb
	},

	/**
	 * Adds a sheet to the workbook
	 *
	 * @param {Workbook} wb
	 * @param {string} label
	 * @param {object} ws a object matching the expected structure for a SheetJS Sheet
	 * @private
	 */
	_addSheetToWorkbook: function (wb, label, ws) {
		// XML escaping of the sheet name is done by the xlsx library
		var safeLabel = label.replace(/:|\/|\?|\*|\\/g, '')
							 .replace('[', '(')
							 .replace(']', ')')

		// #59519 can't have duplicate sheet names
		if (wb.SheetNames.includes(label)) {
			// Find all sheets with the pattern 'label' or 'label n' and
			// increment n
			let regex = new RegExp(`^${safeLabel}( \\d+)?$`)
			let matchingSheets = wb.SheetNames.filter(name => regex.test(name))
			safeLabel += ' ' + (matchingSheets.length + 1)
		}

		wb.SheetNames.push(safeLabel)
		wb.Sheets[safeLabel] = ws
	}
}
export default ExcelExport

ExcelExport.__filename__ = 'components/export/excel-export.js'
